/**
 * TokenLimitsManagement Component
 * 
 * Admin UI for managing token usage limits per user, group, and organization.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Text,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  DefaultButton,
  Dialog,
  DialogType,
  DialogFooter,
  TextField,
  Dropdown,
  IDropdownOption,
  Toggle,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
  IconButton,
  ProgressIndicator,
  Pivot,
  PivotItem,
} from '@fluentui/react';
import {
  MoneyRegular,
  PeopleRegular,
  PersonRegular,
  BuildingRegular,
  AddRegular,
  DeleteRegular,
  ArrowResetRegular,
  EditRegular,
} from '@fluentui/react-icons';
import { tokenLimitsService, TokenLimit, TokenLimitsDashboard } from '../../services/tokenLimitsService';
import { EntraUserPicker, EntraGroupPicker } from '../EntraUserPicker';

interface TokenLimitsManagementProps {
  embedded?: boolean;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
};

const getProgressColor = (percent: number): string => {
  if (percent >= 90) return '#e74c3c';
  if (percent >= 75) return '#f39c12';
  if (percent >= 50) return '#f1c40f';
  return '#27ae60';
};

const LimitTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'user':
      return <PersonRegular style={{ fontSize: 20, color: '#667eea' }} />;
    case 'group':
      return <PeopleRegular style={{ fontSize: 20, color: '#8764b8' }} />;
    case 'organization':
      return <BuildingRegular style={{ fontSize: 20, color: '#107c10' }} />;
    default:
      return <MoneyRegular style={{ fontSize: 20 }} />;
  }
};

const TokenLimitsManagement: React.FC<TokenLimitsManagementProps> = ({ embedded = false }) => {
  const [dashboard, setDashboard] = useState<TokenLimitsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('user');
  const [searchFilter, setSearchFilter] = useState<string>('');
  
  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingLimit, setEditingLimit] = useState<TokenLimit | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Form state - max_tokens_millions is in millions (1 = 1M tokens)
  const [formData, setFormData] = useState({
    limit_type: 'user',
    entity_id: '',
    display_name: '',
    max_tokens_millions: 1,
    reset_period: 'monthly',
    block_at_percent: 100,
    is_active: true,
  });

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tokenLimitsService.getDashboard();
      setDashboard(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load token limits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleCreate = async () => {
    try {
      setSaving(true);
      setError(null);
      
      await tokenLimitsService.createLimit({
        limit_type: formData.limit_type,
        entity_id: formData.entity_id,
        display_name: formData.display_name || formData.entity_id,
        max_tokens: formData.max_tokens_millions * 1000000, // Convert millions to actual tokens
        reset_period: formData.reset_period,
        block_at_percent: formData.block_at_percent,
      });
      
      setSuccess(`Created token limit for ${formData.entity_id}`);
      setShowCreateDialog(false);
      resetForm();
      loadDashboard();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to create token limit');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingLimit) return;
    
    try {
      setSaving(true);
      setError(null);
      
      await tokenLimitsService.updateLimit(
        editingLimit.limit_type,
        editingLimit.entity_id,
        {
          display_name: formData.display_name,
          max_tokens: formData.max_tokens_millions * 1000000, // Convert millions to actual tokens
          reset_period: formData.reset_period as any,
          block_at_percent: formData.block_at_percent,
          is_active: formData.is_active,
        }
      );
      
      setSuccess(`Updated token limit for ${editingLimit.entity_id}`);
      setShowEditDialog(false);
      setEditingLimit(null);
      loadDashboard();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update token limit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (limit: TokenLimit) => {
    if (!window.confirm(`Are you sure you want to delete the token limit for "${limit.display_name}"?`)) {
      return;
    }
    
    try {
      setError(null);
      await tokenLimitsService.deleteLimit(limit.limit_type, limit.entity_id);
      setSuccess(`Deleted token limit for ${limit.display_name}`);
      loadDashboard();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete token limit');
    }
  };

  const handleReset = async (limit: TokenLimit) => {
    if (!window.confirm(`Are you sure you want to reset usage for "${limit.display_name}" to 0?`)) {
      return;
    }
    
    try {
      setError(null);
      await tokenLimitsService.resetUsage(limit.limit_type, limit.entity_id);
      setSuccess(`Reset usage for ${limit.display_name}`);
      loadDashboard();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset usage');
    }
  };

  const openEditDialog = (limit: TokenLimit) => {
    setEditingLimit(limit);
    setFormData({
      limit_type: limit.limit_type,
      entity_id: limit.entity_id,
      display_name: limit.display_name,
      max_tokens_millions: limit.max_tokens / 1000000, // Convert actual tokens to millions
      reset_period: limit.reset_period,
      block_at_percent: limit.block_at_percent,
      is_active: limit.is_active,
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      limit_type: selectedTab,
      entity_id: '',
      display_name: '',
      max_tokens_millions: 1,
      reset_period: 'monthly',
      block_at_percent: 100,
      is_active: true,
    });
  };

  const limitTypeOptions: IDropdownOption[] = [
    { key: 'user', text: 'User' },
    { key: 'group', text: 'Group' },
    { key: 'organization', text: 'Organization' },
  ];

  const resetPeriodOptions: IDropdownOption[] = [
    { key: 'daily', text: 'Daily' },
    { key: 'weekly', text: 'Weekly' },
    { key: 'monthly', text: 'Monthly' },
    { key: 'none', text: 'Never (No Reset)' },
  ];

  const columns: IColumn[] = [
    {
      key: 'status',
      name: '',
      minWidth: 20,
      maxWidth: 20,
      onRender: (item: TokenLimit) => (
        <div style={{ 
          width: 10, 
          height: 10, 
          borderRadius: '50%',
          backgroundColor: item.is_blocked ? '#e74c3c' : item.is_active ? '#27ae60' : '#95a5a6'
        }} />
      ),
    },
    {
      key: 'entity',
      name: 'Entity',
      fieldName: 'display_name',
      minWidth: 100,
      maxWidth: 180,
      isResizable: true,
      onRender: (item: TokenLimit) => (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { overflow: 'hidden' } }}>
          <LimitTypeIcon type={item.limit_type} />
          <Stack styles={{ root: { overflow: 'hidden', minWidth: 0 } }}>
            <Text style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{item.display_name}</Text>
            <Text variant="tiny" style={{ color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{item.entity_id}</Text>
          </Stack>
        </Stack>
      ),
    },
    {
      key: 'usage',
      name: 'Usage',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      onRender: (item: TokenLimit) => (
        <Stack tokens={{ childrenGap: 2 }} styles={{ root: { minWidth: 0 } }}>
          <Stack horizontal horizontalAlign="space-between">
            <Text variant="small">{formatNumber(item.used_tokens)} / {formatNumber(item.max_tokens)}</Text>
            <Text variant="small" style={{ 
              color: getProgressColor(item.percent_used),
              fontWeight: 600
            }}>
              {item.percent_used.toFixed(0)}%
            </Text>
          </Stack>
          <ProgressIndicator 
            percentComplete={item.percent_used / 100}
            barHeight={4}
            styles={{
              progressBar: { 
                background: getProgressColor(item.percent_used)
              }
            }}
          />
        </Stack>
      ),
    },
    {
      key: 'reset',
      name: 'Reset',
      fieldName: 'reset_period',
      minWidth: 55,
      maxWidth: 70,
      onRender: (item: TokenLimit) => (
        <Text variant="small">{item.reset_period === 'none' ? 'Never' : item.reset_period}</Text>
      ),
    },
    {
      key: 'block',
      name: 'Block',
      minWidth: 40,
      maxWidth: 50,
      onRender: (item: TokenLimit) => (
        <Text variant="small">{item.block_at_percent}%</Text>
      ),
    },
    {
      key: 'actions',
      name: '',
      minWidth: 84,
      maxWidth: 84,
      onRender: (item: TokenLimit) => (
        <Stack horizontal tokens={{ childrenGap: 0 }} styles={{ root: { flexShrink: 0 } }}>
          <IconButton
            iconProps={{ iconName: 'Edit' }}
            title="Edit"
            styles={{ root: { width: 26, height: 26, minWidth: 26 } }}
            onClick={() => openEditDialog(item)}
          />
          <IconButton
            iconProps={{ iconName: 'Refresh' }}
            title="Reset Usage"
            styles={{ root: { width: 26, height: 26, minWidth: 26 } }}
            onClick={() => handleReset(item)}
          />
          <IconButton
            iconProps={{ iconName: 'Delete' }}
            title="Delete"
            styles={{ root: { width: 26, height: 26, minWidth: 26, color: '#e74c3c' } }}
            onClick={() => handleDelete(item)}
          />
        </Stack>
      ),
    },
  ];

  if (loading && !dashboard) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spinner size={SpinnerSize.large} label="Loading token limits..." />
      </div>
    );
  }

  const currentLimits = dashboard?.limits[selectedTab as keyof typeof dashboard.limits] || [];
  
  // Filter limits based on search
  const filteredLimits = searchFilter 
    ? currentLimits.filter(limit => 
        limit.display_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        limit.entity_id.toLowerCase().includes(searchFilter.toLowerCase())
      )
    : currentLimits;

  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{ marginBottom: 24 }}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
          <MoneyRegular style={{ fontSize: 28, color: '#667eea' }} />
          <Stack>
            <Text variant="xLarge" style={{ fontWeight: 600 }}>Token Limits</Text>
            <Text variant="small" style={{ color: '#666' }}>
              Manage token usage limits for users, groups, and organizations
            </Text>
          </Stack>
        </Stack>
        
        <PrimaryButton
          text="Add Limit"
          iconProps={{ iconName: 'Add' }}
          onClick={() => {
            resetForm();
            setFormData(prev => ({ ...prev, limit_type: selectedTab }));
            setShowCreateDialog(true);
          }}
        />
      </Stack>

      {/* Messages */}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => setError(null)}
          style={{ marginBottom: 16 }}
        >
          {error}
        </MessageBar>
      )}
      
      {success && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() => setSuccess(null)}
          style={{ marginBottom: 16 }}
        >
          {success}
        </MessageBar>
      )}

      {/* Summary Cards */}
      {dashboard && (() => {
        // Calculate organization-specific stats
        const orgLimits = dashboard.limits.organization || [];
        const orgLimit = orgLimits[0]; // Usually there's one org limit
        const orgUsed = orgLimit?.used_tokens || 0;
        const orgMax = orgLimit?.max_tokens || 0;
        const orgPercent = orgMax > 0 ? (orgUsed / orgMax * 100) : 0;
        
        return (
          <Stack horizontal tokens={{ childrenGap: 16 }} style={{ marginBottom: 24 }} wrap>
            {/* Organization Progress - Main Card */}
            <div style={{
              flex: 2,
              minWidth: 280,
              padding: 20,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 12,
              color: 'white'
            }}>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
                <div>
                  <Text variant="small" style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: 4 }}>
                    Organization Usage
                  </Text>
                  <Text style={{ fontSize: 28, fontWeight: 700, color: 'white' }}>
                    {orgMax > 0 ? formatNumber(orgUsed) : 'Not Set'}
                  </Text>
                  {orgMax > 0 && (
                    <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
                      / {formatNumber(orgMax)} tokens
                    </Text>
                  )}
                </div>
                <Text style={{ fontSize: 24, fontWeight: 700, color: 'white' }}>
                  {orgMax > 0 ? `${orgPercent.toFixed(1)}%` : '--'}
                </Text>
              </Stack>
              {orgMax > 0 && (
                <ProgressIndicator
                  percentComplete={orgPercent / 100}
                  barHeight={8}
                  styles={{
                    root: { marginTop: 12 },
                    progressBar: { background: 'white' },
                    progressTrack: { background: 'rgba(255,255,255,0.3)' }
                  }}
                />
              )}
            </div>
            
            {/* Users Count */}
            <div style={{
              padding: 20,
              background: '#e3f2fd',
              borderRadius: 12,
              minWidth: 100,
              textAlign: 'center'
            }}>
              <PersonRegular style={{ fontSize: 24, color: '#1976d2', marginBottom: 4 }} />
              <Text style={{ fontSize: 24, fontWeight: 700, color: '#1976d2', display: 'block' }}>
                {dashboard.summary.by_type_count.user || 0}
              </Text>
              <Text variant="small" style={{ color: '#666' }}>User Limits</Text>
            </div>
            
            {/* Groups Count */}
            <div style={{
              padding: 20,
              background: '#f3e5f5',
              borderRadius: 12,
              minWidth: 100,
              textAlign: 'center'
            }}>
              <PeopleRegular style={{ fontSize: 24, color: '#7b1fa2', marginBottom: 4 }} />
              <Text style={{ fontSize: 24, fontWeight: 700, color: '#7b1fa2', display: 'block' }}>
                {dashboard.summary.by_type_count.group || 0}
              </Text>
              <Text variant="small" style={{ color: '#666' }}>Group Limits</Text>
            </div>
            
            {/* Blocked Count */}
            <div style={{
              padding: 20,
              background: dashboard.summary.blocked_count > 0 ? '#fee2e2' : '#d1fae5',
              borderRadius: 12,
              minWidth: 100,
              textAlign: 'center'
            }}>
              <Text style={{ 
                fontSize: 24, 
                fontWeight: 700, 
                color: dashboard.summary.blocked_count > 0 ? '#e74c3c' : '#27ae60',
                display: 'block'
              }}>
                {dashboard.summary.blocked_count}
              </Text>
              <Text variant="small" style={{ color: '#666' }}>Blocked</Text>
            </div>
          </Stack>
        );
      })()}

      {/* Tabs */}
      <Pivot
        selectedKey={selectedTab}
        onLinkClick={(item) => {
          setSelectedTab(item?.props.itemKey || 'user');
          setSearchFilter(''); // Clear search when switching tabs
        }}
        style={{ marginBottom: 16 }}
      >
        <PivotItem 
          headerText={`Users (${dashboard?.summary.by_type_count.user || 0})`} 
          itemKey="user" 
          itemIcon="Contact"
        />
        <PivotItem 
          headerText={`Groups (${dashboard?.summary.by_type_count.group || 0})`} 
          itemKey="group" 
          itemIcon="Group"
        />
        <PivotItem 
          headerText={`Organizations (${dashboard?.summary.by_type_count.organization || 0})`} 
          itemKey="organization" 
          itemIcon="Org"
        />
      </Pivot>

      {/* Search Box */}
      {(selectedTab === 'user' || selectedTab === 'group') && currentLimits.length > 0 && (
        <TextField
          placeholder={selectedTab === 'user' ? 'Search users by name or email...' : 'Search groups by name...'}
          iconProps={{ iconName: 'Search' }}
          value={searchFilter}
          onChange={(_, newValue) => setSearchFilter(newValue || '')}
          styles={{
            root: { marginBottom: 12, maxWidth: 400 }
          }}
        />
      )}

      {/* Limits Table */}
      {filteredLimits.length === 0 && searchFilter ? (
        <div style={{
          padding: 40,
          textAlign: 'center',
          background: '#f8f9fa',
          borderRadius: 12
        }}>
          <Text variant="large" style={{ color: '#666' }}>
            No results found for "{searchFilter}"
          </Text>
          <br />
          <DefaultButton
            text="Clear Search"
            iconProps={{ iconName: 'Clear' }}
            onClick={() => setSearchFilter('')}
            style={{ marginTop: 16 }}
          />
        </div>
      ) : filteredLimits.length === 0 ? (
        <div style={{
          padding: 40,
          textAlign: 'center',
          background: '#f8f9fa',
          borderRadius: 12
        }}>
          <Text variant="large" style={{ color: '#666' }}>
            No {selectedTab} limits configured
          </Text>
          <br />
          <DefaultButton
            text="Add First Limit"
            iconProps={{ iconName: 'Add' }}
            onClick={() => {
              resetForm();
              setFormData(prev => ({ ...prev, limit_type: selectedTab }));
              setShowCreateDialog(true);
            }}
            style={{ marginTop: 16 }}
          />
        </div>
      ) : (
        <div style={{ overflowX: 'hidden' }}>
          <DetailsList
            items={filteredLimits}
            columns={columns}
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
            isHeaderVisible={true}
            styles={{
              root: { overflowX: 'hidden' },
              contentWrapper: { overflowX: 'hidden' }
            }}
          />
        </div>
      )}

      {/* Create Dialog */}
      <Dialog
        hidden={!showCreateDialog}
        onDismiss={() => setShowCreateDialog(false)}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Create Token Limit',
        }}
        modalProps={{ isBlocking: true }}
        minWidth={600}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          <Dropdown
            label="Limit Type"
            selectedKey={formData.limit_type}
            options={limitTypeOptions}
            onChange={(_, option) => setFormData(prev => ({ 
              ...prev, 
              limit_type: option?.key as string,
              entity_id: '',
              display_name: ''
            }))}
          />
          
          {/* User selection via EntraUserPicker */}
          {formData.limit_type === 'user' && (
            <EntraUserPicker
              label="Select User"
              selectedEmails={formData.entity_id ? [formData.entity_id] : []}
              onSelectionChange={(emails) => {
                const email = emails[0] || '';
                setFormData(prev => ({ 
                  ...prev, 
                  entity_id: email,
                  display_name: prev.display_name || email 
                }));
              }}
              maxSelection={1}
              placeholder="Search for a user by name or email..."
              description="Search and select a user from Entra ID"
            />
          )}
          
          {/* Group selection via EntraGroupPicker */}
          {formData.limit_type === 'group' && (
            <EntraGroupPicker
              label="Select Group"
              selectedGroupIds={formData.entity_id ? [formData.entity_id] : []}
              onSelectionChange={(groupIds) => {
                const groupId = groupIds[0] || '';
                setFormData(prev => ({ 
                  ...prev, 
                  entity_id: groupId,
                  display_name: prev.display_name || groupId
                }));
              }}
              placeholder="Search for a group by name..."
              description="Search and select a group from Entra ID"
            />
          )}
          
          {/* Organization domain - simple text field */}
          {formData.limit_type === 'organization' && (
            <TextField
              label="Organization Domain"
              placeholder="company.com"
              value={formData.entity_id}
              onChange={(_, value) => setFormData(prev => ({ 
                ...prev, 
                entity_id: value || '',
                display_name: prev.display_name || value || ''
              }))}
              required
              description="Enter the email domain for the organization (e.g., company.com)"
            />
          )}
          
          <TextField
            label="Display Name"
            placeholder="Friendly name for display"
            value={formData.display_name}
            onChange={(_, value) => setFormData(prev => ({ ...prev, display_name: value || '' }))}
          />
          
          <TextField
            label="Max Tokens (Millions)"
            type="number"
            value={formData.max_tokens_millions.toString()}
            onChange={(_, value) => setFormData(prev => ({ 
              ...prev, 
              max_tokens_millions: parseFloat(value || '0') 
            }))}
            suffix="M tokens"
            description="Enter limit in millions (e.g., 1 = 1,000,000 tokens)"
            required
          />
          
          <Dropdown
            label="Reset Period"
            selectedKey={formData.reset_period}
            options={resetPeriodOptions}
            onChange={(_, option) => setFormData(prev => ({ 
              ...prev, 
              reset_period: option?.key as string 
            }))}
          />
          
          <TextField
            label="Block At Percent"
            type="number"
            value={formData.block_at_percent.toString()}
            onChange={(_, value) => setFormData(prev => ({ 
              ...prev, 
              block_at_percent: parseInt(value || '100', 10) 
            }))}
            suffix="%"
            description="User will be blocked when usage reaches this percentage"
          />
        </Stack>
        
        <DialogFooter>
          <PrimaryButton 
            text={saving ? 'Creating...' : 'Create'} 
            onClick={handleCreate}
            disabled={saving || !formData.entity_id || !formData.max_tokens_millions}
          />
          <DefaultButton text="Cancel" onClick={() => setShowCreateDialog(false)} />
        </DialogFooter>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        hidden={!showEditDialog}
        onDismiss={() => setShowEditDialog(false)}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: `Edit Token Limit: ${editingLimit?.display_name}`,
        }}
        modalProps={{ isBlocking: true }}
        minWidth={500}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          <TextField
            label="Display Name"
            value={formData.display_name}
            onChange={(_, value) => setFormData(prev => ({ ...prev, display_name: value || '' }))}
          />
          
          <TextField
            label="Max Tokens (Millions)"
            type="number"
            value={formData.max_tokens_millions.toString()}
            onChange={(_, value) => setFormData(prev => ({ 
              ...prev, 
              max_tokens_millions: parseFloat(value || '0') 
            }))}
            suffix="M tokens"
            description="Enter limit in millions (e.g., 1 = 1,000,000 tokens)"
          />
          
          <Dropdown
            label="Reset Period"
            selectedKey={formData.reset_period}
            options={resetPeriodOptions}
            onChange={(_, option) => setFormData(prev => ({ 
              ...prev, 
              reset_period: option?.key as string 
            }))}
          />
          
          <TextField
            label="Block At Percent"
            type="number"
            value={formData.block_at_percent.toString()}
            onChange={(_, value) => setFormData(prev => ({ 
              ...prev, 
              block_at_percent: parseInt(value || '100', 10) 
            }))}
            suffix="%"
          />
          
          <Toggle
            label="Active"
            checked={formData.is_active}
            onChange={(_, checked) => setFormData(prev => ({ ...prev, is_active: !!checked }))}
          />
        </Stack>
        
        <DialogFooter>
          <PrimaryButton 
            text={saving ? 'Saving...' : 'Save'} 
            onClick={handleUpdate}
            disabled={saving}
          />
          <DefaultButton text="Cancel" onClick={() => setShowEditDialog(false)} />
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default TokenLimitsManagement;
